import React, { type JSX } from 'react';
import { observer } from 'mobx-react';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { useLocation, useParams, useNavigate, type NavigateFunction } from 'react-router-dom';
import { ImgSafe } from '../components/Images/ImgSafe';
import { ImgMenu } from '../components/Images/ImgMenu';
import { ImgTimes } from '../components/Images/ImgTimes';

import { type DragEndEvent } from '@dnd-kit/core';

const untitledNoteTitle = 'Untitled note';

import { type CategoryInfo } from '../models/CategoryInfo';
import { type NotesResponse } from '../models/NotesResponse';
import { type NoteInfo } from '../models/NoteInfo';
import { type AdvancedSearchResult } from '../models/SearchResult';
import { PromiseCompletion } from '../classes/PromiseCompletion';

import { apiService } from '../services/ApiService';
import { notificationService } from '../services/NotificationService';
import { syncService, actualizeWhileOnline } from '../services/SyncService';
import { ArrayService } from '../services/ArrayService';
import { searchService } from '../services/SearchService';
import { extensions } from '@services/Extensions';

const RenameNoteDialog = React.lazy(() => import('./dialogs/RenameNoteDialog'));
import { type RenameNoteDialogProps } from './dialogs/RenameNoteDialog';

const ColorNoteDialog = React.lazy(() => import('./dialogs/ColorNoteDialog'));
import { type ColorNoteDialogProps } from './dialogs/ColorNoteDialog';

const ShareNoteDialog = React.lazy(() => import('./dialogs/ShareNoteDialog'));
import { type ShareNoteDialogProps } from './dialogs/ShareNoteDialog';

const ChangeCategoryDialog = React.lazy(() => import('./dialogs/ChangeCategoryDialog'));
import { type ChangeCategoryDialogProps, type ChangeCategoryNoteParameters } from './dialogs/ChangeCategoryDialog';

import Page404 from './Page404';

const EditCategoryDialog = React.lazy(() => import('./dialogs/EditCategoryDialog'));
import { type EditCategoryDialogProps } from './dialogs/EditCategoryDialog';

const AddCategoryDialog = React.lazy(() => import('./dialogs/AddCategoryDialog'));
import { type AddCategoryDialogProps } from './dialogs/AddCategoryDialog';

const NoteHistoryDialog = React.lazy(() => import('./dialogs/NoteHistoryDialog'));
import { type NoteHistoryDialogProps } from './dialogs/NoteHistoryDialog';

import { ContextMenuItem, ContextMenuSeparator } from '../components/ContextMenu';
import { ModalButtonType, modalService } from '../components/Modal';
import { Skeleton } from '../components/Skeleton';
import Editor from '../components/Editor';
import NewNoteView from '../components/NewNoteView';
import NoteSidebar from '../components/NoteSidebar';
import { categoryRailStore } from '@services/CategoryRailStore';

interface NotesWatcherProps {
  onUrl: (splat: string) => void;
  onCategory: (id: string) => void;
}

/** Bridges the URL into the class-based Notes page: the path splat (/n/<id>, /n/new)
 *  drives which note is open; legacy query deep-links (?new=1, ?cat=, ?q=) are
 *  consumed and stripped. Works on mount and client-side navigation. */
function NotesWatcher({ onUrl, onCategory }: NotesWatcherProps): null {
  const location = useLocation();
  const params = useParams();

  React.useEffect(() => {
    const query = new URLSearchParams(location.search);
    const cat = query.get('cat');
    const hasQ = query.has('q');
    // Path splat wins (e.g. /n/new → 'new', /n/<id> → '<id>'); legacy ?new=1 is a fallback.
    let splat = params['*'] ?? '';
    if (splat === '' && query.get('new') === '1') {
      splat = 'new';
    }
    if (cat) {
      query.delete('cat');
      onCategory(cat);
    }
    if (hasQ) {
      searchService.consumeQueryParam();
    }
    onUrl(splat);
    if (cat) {
      history.replaceState(null, '', location.pathname + (query.toString() ? '?' + query.toString() : ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, params['*']]);

  return null;
}

/** Bridges `useNavigate` (a hook) into the class-based Notes page. */
function RouterBridge({ onReady }: { onReady: (navigate: NavigateFunction) => void }): null {
  const navigate = useNavigate();
  React.useEffect(() => {
    onReady(navigate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);
  return null;
}

@observer
export default class Notes extends React.Component<unknown> {
  @observable private _notes: NoteInfo[] = [];
  @observable private _categories: CategoryInfo[] = [];
  @observable private _activeCategoryId = '';
  @observable private _activeNoteId = '';
  @observable private _isArchive = false;
  @observable private _onlyPinned = false;
  @observable private _sortNewest = false;
  @observable private _isUnlocked = false;
  @observable private _editorHeaderHeight = 41;
  @observable private _isMobileSidebarVisible = false;
  @observable private _isListHidden = false;
  // Top search → in-app search mode (driven by the shared searchService; category rail stays)
  @observable.ref private _searchResults: AdvancedSearchResult[] | null = null;
  @observable private _isSearching = false;
  // New-note page (replaces the old create-note modal)
  @observable private _isNewNoteOpen = false;
  // True until the first /api/note load resolves — drives the skeleton loaders.
  @observable private _isInitialLoading = true;
  private _loader: PromiseCompletion = new PromiseCompletion();
  private _notesListRef: React.RefObject<HTMLDivElement> = React.createRef();
  private _searchCancellationSource: AbortController;
  private _pendingCategoryId: string | null = null;
  private _pendingNoteId: string | null = null;
  private _navigate: NavigateFunction | null = null;
  private _searchReaction: (() => void) | null = null;
  private _railStateReaction: (() => void) | null = null;

  constructor(props: unknown){
    super(props);
    makeObservable(this);
    this._searchCancellationSource = new AbortController();
  }

  async componentDidMount(): Promise<void>{
    // If a specific note is in the URL (/n/<id>), remember it so _loadData can select it
    // once the list arrives. Set before the async load to avoid a race with the watcher.
    const splat = window.location.pathname.replace(/^\/n\/?/, '');
    if (splat && splat !== 'new') {
      runInAction(() => { this._pendingNoteId = splat; });
    }
    // The "All notes" rail item navigates to /n/<lastNoteId>; honour that intent by
    // forcing the "All notes" category (not the note's own category) on this load.
    if (categoryRailStore.pendingAllNotes) {
      categoryRailStore.clearPendingAllNotes();
      runInAction(() => { this._pendingCategoryId = ''; });
    }
    await this._loadData(true);
    setTimeout(this._recalculateScrollSize, 100);
    window.addEventListener('resize', this._onWindowResize);
    window.addEventListener('blur', this._onBlur);

    // Live search: react to the shared top-search box (debounced), so results update
    // as the user types. Watching the sequence counter re-runs the same query;
    // fireImmediately covers a query set before this view mounted.
    this._searchReaction = reaction(
      () => `${searchService.query}#${searchService.sequence}`,
      () => extensions.executeTimeout(this._performSearch, 250),
      { fireImmediately: true }
    );

    // Bridge into the shared rail (AppShell): register stable handlers once, then
    // mirror live state into the store via a reaction.
    categoryRailStore.setMode('notes');
    void categoryRailStore.load();
    categoryRailStore.setNotesHandlers({
      onAllClick: () => this._onCategoryChanged({ id: '', name: 'All notes', sortOrder: 0 }),
      onCategoryClick: this._onCategoryChanged,
      onAddCategory: this._onAddCategoryClick,
      onCategoryDragEnd: this._handleCategoryDragEnd,
      onCategoryEdit: this._onCategoryEdit
    });
    this._railStateReaction = reaction(
      () => [
        this._activeCategoryId,
        this._activeNoteId,
        this._isArchive,
        this._isSearchMode,
        this._searchResults?.length ?? 0,
        this._totalNoteCount,
        JSON.stringify(this._noteCounts),
        searchService.query
      ],
      () => {
        categoryRailStore.setNotesState({
          activeCategoryId: this._activeCategoryId,
          lastNoteId: this._activeNoteId || undefined,
          isArchive: this._isArchive,
          isSearchMode: this._isSearchMode,
          searchQuery: searchService.query,
          searchCount: this._searchResults?.length ?? 0,
          noteCounts: this._noteCounts,
          totalNoteCount: this._totalNoteCount
        });
      },
      { fireImmediately: true }
    );
  }

  /** Router hook bridge → hand the navigate function to the class. */
  private _onNavigateReady = (navigate: NavigateFunction): void => {
    this._navigate = navigate;
  };

  /** URL → state. /n/new opens the new-note form; /n/<id> opens that note. */
  @action.bound
  private _onWatcherUrl(splat: string): void {
    if (splat === 'new') {
      this._isNewNoteOpen = true;
      // On mobile the note list is a fly-out drawer — close it so the new-note
      // form (which fills the editor pane) is actually visible.
      this._closeMobileSidebar();
      return;
    }
    if (this._isNewNoteOpen) {
      this._isNewNoteOpen = false;
    }
    if (splat) {
      this._selectNoteFromUrl(splat);
    }
  }

  /** Opens a note from the URL; defers to _loadData if the list isn't loaded yet. */
  @action
  private _selectNoteFromUrl(id: string): void {
    const note = this._notes.find(n => n.id === id);
    if (note) {
      this._activeNoteId = id;
      this._isUnlocked = false;
      this._setTitle(note.name);
    } else {
      this._pendingNoteId = id;
    }
  }

  /** Deep-link: ?cat=<id> → select that category (from the Dashboard rail). */
  @action.bound
  private _onWatcherCategory(id: string): void {
    this._pendingCategoryId = id;
    if (this._notes.length > 0) {
      this._activeCategoryId = id;
      this._pendingCategoryId = null;
    }
  }

  componentWillUnmount(){
    window.removeEventListener('resize', this._onWindowResize);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    window.removeEventListener('blur', this._onBlur);
    document.body.classList.remove('mobile-sidebar-open');
    this._searchReaction?.();
    this._railStateReaction?.();
    this._searchCancellationSource.abort();
    categoryRailStore.clearNotes();
  }

  render(): JSX.Element{
    const notes = this.Notes;
    const sortedNotes = [...notes].sort((n1, n2) => {
      const p1 = !!n1.isPinned, p2 = !!n2.isPinned;
      if (p1 !== p2) return p1 ? -1 : 1;
      if (this._sortNewest) {
        const d1 = n1.lastWrite ? new Date(n1.lastWrite).getTime() : 0;
        const d2 = n2.lastWrite ? new Date(n2.lastWrite).getTime() : 0;
        if (d1 !== d2) return d2 - d1;
      }
      return (n1.sortOrder ?? 0) - (n2.sortOrder ?? 0);
    });
    const isTouchDevice = this._isTouchDevice();

    const activeNote = this._notes.find(x => x.id === this._activeNoteId);

    return (
      <div className={`notes-container${this._isListHidden ? ' list-hidden' : ''}`}>
        <NotesWatcher onUrl={this._onWatcherUrl} onCategory={this._onWatcherCategory} />
        <RouterBridge onReady={this._onNavigateReady} />
        {/* Mobile overlay for the note-list drawer (notes mode) */}
        <div
          className={`mobile-sidebar-overlay${this._isMobileSidebarVisible ? ' visible' : ''}`}
          onClick={this._onToggleMobileSidebar}
        />

        {/* Pane 2: Note List */}
        <NoteSidebar
          notes={sortedNotes}
          categories={this._categories}
          isArchive={this._isArchive}
          onlyPinned={this._onlyPinned}
          sortNewest={this._sortNewest}
          activeCategoryId={this._activeCategoryId}
          activeNoteId={this._activeNoteId}
          isMobileSidebarVisible={this._isMobileSidebarVisible}
          isTouchDevice={isTouchDevice}
          containerRef={this._notesListRef}
          isLoading={this._isInitialLoading}
          searchQuery={searchService.query}
          searchResults={this._searchResults}
          isSearching={this._isSearching}
          onSelectSearchResult={this._onSelectSearchResult}
          onClearSearch={this._onClearSearch}
          onArchiveToggle={this._onArchiveToggle}
          onPinnedToggle={this._onPinnedToggle}
          onNewestToggle={this._onNewestToggle}
          onNoteMouseDown={this._onNoteMouseDown}
          onCollapse={this._toggleList}
          onCategoryChanged={this._onCategoryChanged}
          onDragEnd={this._handleDragEnd}
          onRenderContextMenu={this._renderMenu}
        />

        {/* Desktop re-open handle for the note list (only when collapsed). The
            rail's re-open handle lives in the shared rail. */}
        {this._isListHidden && (
          <button className="pane-handle pane-handle-list" title="Show notes" aria-label="Show notes" onClick={this._toggleList}>
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
          </button>
        )}

        {/* Mobile sidebar toggle button */}
        <button
          className="mobile-sidebar-toggle"
          onClick={this._onToggleMobileSidebar}
          title={this._isMobileSidebarVisible ? 'Close menu' : 'Open menu'}
        >
          {this._isMobileSidebarVisible ? <ImgTimes size={18} /> : <ImgMenu size={18} />}
        </button>

        {/* Pane 3: Editor */}
        <div className="note-area">
          <style>
            {`#snow-container .ql-container {  height: calc(100vh - var(--app-nav-height) - ${this._editorHeaderHeight}px) !important; height: calc(var(--vh, 1vh) * 100 - var(--app-nav-height) - ${this._editorHeaderHeight}px)!important; }`}
          </style>
          {this._isNewNoteOpen ? (
            <NewNoteView
              categories={this._categories}
              defaultCategoryId={this._activeCategoryId}
              onCreateCategory={this._createCategory}
              onCancel={this._onCancelNewNote}
              onSave={this._onSaveNewNote}
            />
          ) : this._isInitialLoading ? (
            <div className="editor-skeleton">
              <Skeleton height={44} borderRadius={0} />
              <div className="editor-skeleton-body">
                <Skeleton height={22} width="40%" />
                <Skeleton height={14} width="100%" />
                <Skeleton height={14} width="96%" />
                <Skeleton height={14} width="90%" />
                <Skeleton height={14} width="94%" />
                <Skeleton height={14} width="62%" />
              </div>
            </div>
          ) : (!!this._activeNoteId && <Editor
            key={this._activeNoteId}
            noteId={this._activeNoteId}
            notFoundView={Page404}
            sharedView={false}
            onDataLoaded={() => setTimeout(this._onWindowResize, 0)}
          />)}
          {/* Hide the lock overlay while the new-note form is shown: _activeNoteId
              still points at the (protected) note we came from, and the overlay
              would otherwise paint over the form. */}
          {!this._isNewNoteOpen && activeNote?.isProtected && !this._isUnlocked && (
            <div className="protected-screen">
              <div className="protected-card">
                <div className="protected-icon"><ImgSafe size={64} className="safe-icon" /></div>
                <h2 className="protected-title">This note is protected</h2>
                <p className="protected-sub">Unlock to read its contents</p>
                <button className="btn btn-primary protected-unlock" onClick={this._onUnlockClick}>
                  Unlock
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  private _renderMenu = (element: HTMLElement): JSX.Element | null => {
    const activityCodeTarget = element.closest('.note-item') as HTMLDivElement;
    if (activityCodeTarget){
      const id = activityCodeTarget.dataset.id as string;
      const note = this._notes.find(x => x.id === id);

      return (
        <>
          <ContextMenuItem title="Rename note" onClick={() => this._renameNote(id)} />
          <ContextMenuItem title="Share" onClick={() => this._shareNote(id)} />
          <ContextMenuItem title="Color" onClick={() => this._colorNote(id)} />
          <ContextMenuItem title={note?.isProtected ? "Unprotect" : "Protect"} onClick={() => this._toggleProtected(id, !!note?.isProtected)} />
          <ContextMenuItem title={note?.isPinned ? "Unpin" : "Pin"} onClick={() => this._togglePinned(id)} />
          <ContextMenuSeparator/>
          <ContextMenuItem title="History" onClick={() => this._openHistoryDialog(id)} />
          <ContextMenuItem title="Category" onClick={() => this._moveToCategory(id)} />
          {!note?.isArchived && <ContextMenuItem title="Archive" onClick={() => this._archiveNote(id)} />}
          {note?.isArchived && <ContextMenuItem title="Undo Archive" onClick={() => this._undoArchiveNote(id)} />}
          <ContextMenuSeparator/>
          <ContextMenuItem title="Delete note" onClick={() => this._deleteNote(id)} />
        </>
      );
    }

    return null;
  }

  private _isTouchDevice() {
    return (('ontouchstart' in window) ||  (navigator.maxTouchPoints > 0));
  }

  @computed
  private get _isSearchMode(): boolean {
    return this._searchResults !== null || this._isSearching;
  }

  @computed
  private get Notes() {
    // '' / null = "All notes"; 'default' = virtual category for notes without a categoryId.
    const active = this._activeCategoryId ?? '';
    let notes = this._notes;
    if (active === 'default') {
      notes = notes.filter(x => !x.categoryId);
    } else if (active !== '') {
      notes = notes.filter(x => x.categoryId === active);
    }
    if (this._onlyPinned) {
      notes = notes.filter(x => !!x.isPinned);
    }
    return notes;
  }

  @computed
  private get _noteCounts(): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const n of this._notes) {
      const key = n.categoryId ?? '';
      counts[key] = (counts[key] ?? 0) + 1;
    }
    return counts;
  }

  @computed
  private get _totalNoteCount(): number {
    return this._notes.length;
  }

  @action.bound
  private _toggleList(){
    // At the fly-out breakpoint the sidebar is the mobile drawer, so the header
    // collapse button toggles the fly-out; on desktop it collapses pane width.
    if (window.innerWidth <= 860) {
      this._onToggleMobileSidebar();
    } else {
      this._isListHidden = !this._isListHidden;
    }
  }

  private _onWindowResize = () => {
    this._recalculateScrollSize();
  }

  @action
  private _recalculateScrollSize = () => {
    const header = document.getElementsByClassName('ql-toolbar')[0];
    this._editorHeaderHeight = header?.getBoundingClientRect()?.height || 41;
  }

  /** + icon in the rail header → create a new category. */
  @action.bound
  private async _onAddCategoryClick(){
    await modalService.show<AddCategoryDialogProps, void>(AddCategoryDialog, {
      onSaved: () => this._refreshCategories()
    });
  }

  /** Pencil icon on a rail category → edit name/color/remove. */
  @action.bound
  private async _onCategoryEdit(category: CategoryInfo): Promise<void> {
    await modalService.show<EditCategoryDialogProps, void>(EditCategoryDialog, {
      category: category,
      noteCount: category.count ?? this._noteCounts[category.id] ?? 0,
      onSaved: () => this._refreshCategories()
    });
  }

  /** Creates a real category (used by the new-note view when the user has none). */
  private async _createCategory(name: string): Promise<string> {
    const created = await apiService.post<CategoryInfo>('/api/note/category', { name: name }, {
      loader: this._loader
    });
    await this._refreshCategories();
    return created.id;
  }
  
  @action.bound
  private _onNoteMouseDown(e: React.MouseEvent, note: NoteInfo){
    if (e.button === 1 /* Middle */) {
      e.preventDefault();
      e.stopPropagation();
      window.open('/n/' + note.id , '_blank')?.focus();
      return;
    }

    this._activeNoteId = note.id;
    this._isUnlocked = false;
    this._setTitle(note.name);
    this._navigate?.('/n/' + note.id);
    
    const isRightClick = e.button === 2;

    // Close mobile sidebar when selecting a note
    // check for .context-menu-target as target and ignore if click is on it, to allow opening context menu without closing sidebar
    if (!isRightClick && window.innerWidth <= 860 && !((e.target as HTMLElement).closest('.context-menu-target'))) {
      this._isMobileSidebarVisible = false;
    }
  }

  /** Closes the mobile note-list drawer and releases the body scroll lock. */
  @action
  private _closeMobileSidebar(): void {
    this._isMobileSidebarVisible = false;
    if (window.innerWidth <= 860) {
      document.body.classList.remove('mobile-sidebar-open');
    }
  }

  @action.bound
  private _onToggleMobileSidebar(){
    if (this._isMobileSidebarVisible) {
      this._closeMobileSidebar();
      return;
    }
    this._isMobileSidebarVisible = true;
    // Prevent body scrolling while the drawer is open on mobile.
    if (window.innerWidth <= 860) {
      document.body.classList.add('mobile-sidebar-open');
    }
  }

  @action.bound
  private _onUnlockClick(){
    this._isUnlocked = true;
  }

  @action.bound
  private _onVisibilityChange(){
    if (document.visibilityState == 'hidden') {
      this._isUnlocked = false;
    }
  }

  @action.bound
  private _onBlur(){
    this._isUnlocked = false;
  }

  @action.bound
  private async _onArchiveToggle(){
    if (this._loader.isPending) return;

    this._isArchive = !this._isArchive;
    await this._loadData(false);
    if (searchService.isActive) {
      this._performSearch();
    }
  }

  @action.bound
  private async _onCategoryChanged(e: CategoryInfo){
    // Choosing a category (or "All notes") exits search mode.
    this._onClearSearch();
    this._activeCategoryId = e.id;
  }

  private async _loadData(init: boolean): Promise<void>{
    const parameters = { 
      isArchive: init ? null : (this._isArchive ? 'true' : 'false') 
    };

    const notesResponse = await apiService.get<NotesResponse>('/api/note', parameters, {
      loader: this._loader
    });

    runInAction(() => {
      // A note referenced by the URL (/n/<id>) wins over the server's last-active note.
      // Normalize null → '' so "All notes" (active note has no category) is treated as the empty key.
      const pendingNoteId = this._pendingNoteId;
      const activeNoteId = (pendingNoteId && notesResponse.notes.some(n => n.id === pendingNoteId) ? pendingNoteId
        : notesResponse.activeNoteId) ?? '';
      this._pendingNoteId = null;

      this._activeCategoryId = this._pendingCategoryId ?? notesResponse.activeCategoryId ?? '';
      this._pendingCategoryId = null;

      this._categories = this._appendDefaultCategory(notesResponse.categories, notesResponse.notes);
      this._notes = notesResponse.notes;
      this._activeNoteId = activeNoteId;
      this._isInitialLoading = false;
      
      if (init){
        this._isArchive = !!notesResponse.activeNoteArchived;
        const activeNote = this._notes.find(x => x.id === this._activeNoteId);
        if (activeNote){
          this._setTitle(activeNote.name);
        }
      }
    });

    // Actualize only on the initial load; archive/category toggles are user actions.
    if (init) {
      syncService.markCached();
      actualizeWhileOnline(async () => {
        const fresh = await apiService.get<NotesResponse>('/api/note', parameters, { actualize: true });
        runInAction(() => {
          this._categories = this._appendDefaultCategory(fresh.categories, fresh.notes);
          this._notes = fresh.notes;
          if (!fresh.notes.some(n => n.id === this._activeNoteId)) {
            this._activeNoteId = (fresh.activeNoteId ?? '');
          }
        });
      });
    }
  }

  private async _refreshCategories(){
    const categories = await apiService.get<CategoryInfo[]>('/api/note/category', null, {
      loader: this._loader
    });

    runInAction(() => {
      this._categories = this._appendDefaultCategory(categories, this._notes);
      // If the active virtual category no longer has uncategorized notes, fall back to All notes.
      if (this._activeCategoryId === 'default' && !this._categories.some(c => c.id === 'default')) {
        this._activeCategoryId = '';
      }
    });
    // Keep the shared rail (AppShell) in sync with the new order/counts.
    void categoryRailStore.refresh();
  }

  /** Appends the virtual "Default" row (id 'default') for legacy uncategorized notes. */
  private _appendDefaultCategory(categories: CategoryInfo[], notes: NoteInfo[]): CategoryInfo[] {
    if (notes.find(x => !x.categoryId) && !categories.some(c => c.id === 'default')){
      categories.push({
        id: 'default',
        name: 'Default',
        sortOrder: 0
      });
    }
    return categories;
  }

  private _setTitle(noteName: string){
    document.title = 'Notes - ' + noteName;
  }

  @action.bound
  private async _deleteNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation("Confirmation", `Do you really want to delete note (${note.name ?? untitledNoteTitle})`, { color: 'danger', buttonTitle: 'Remove' })){
      return;
    }

    await apiService.delete('/api/note/' + id, {
      loader: this._loader
    });

    const noteIndex = this._notes.findIndex(n => n.id === id);
    if (noteIndex !== -1){
      this._notes.splice(noteIndex, 1);
    }
    const activeNote = this._notes[0];
    if (activeNote){
      this._setTitle(activeNote.name);
    }
    this._activeNoteId = activeNote?.id;
    this._navigate?.(activeNote ? '/n/' + activeNote.id : '/notes');
  }

  @action.bound
  private async _renameNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }
    
    const { button, result } = await modalService.show<RenameNoteDialogProps, string>(RenameNoteDialog, {
      note: note
    });

    if (button !== ModalButtonType.Ok || !result){
      return;
    }
    
    await apiService.put('/api/note/' + id + '/name', { name: result }, {
      loader: this._loader
    });

    note.name = result;
  }
  
  @action.bound
  private async _colorNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }
    
    const { button, result } = await modalService.show<ColorNoteDialogProps, string>(ColorNoteDialog, {
      note: note
    });

    if (button !== ModalButtonType.Ok || !result){
      return;
    }
    
    await apiService.put<NoteInfo>('/api/note/' + id + '/color', { color: result }, {
      loader: this._loader
    });

    note.color = result;
  }

  @action.bound
  private async _shareNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    await modalService.show<ShareNoteDialogProps, string>(ShareNoteDialog, {
      note: note,
      onUpdate: (e) => {
        note.isPrivate = !e.shareId;
        note.shareId = e.shareId;
        note.shareAsReadOnly = e.isReadOnly;
      }
    });

  }

  @action.bound
  private async _openHistoryDialog(id: string): Promise<void>{
    await modalService.show<NoteHistoryDialogProps, void>(NoteHistoryDialog, {
      noteId: id
    });
  }

  @action.bound
  private async _archiveNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to archive note ${note.name}?`)) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/archive', null, {
      loader: this._loader
    });

    let activeNote: NoteInfo | undefined;
    const noteIndex = this._notes.findIndex(x => x.id === note.id);
    if (noteIndex !== -1){
      this._notes.splice(noteIndex, 1);
      activeNote = this._notes[noteIndex];
      this._activeNoteId = activeNote?.id;
      activeNote && this._setTitle(activeNote.name);
    }
    this._navigate?.(activeNote ? '/n/' + activeNote.id : '/notes');
  }

  @action.bound
  private async _moveToCategory(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    const { button, result } = await modalService.show<ChangeCategoryDialogProps, ChangeCategoryNoteParameters>(ChangeCategoryDialog, {
      note: note
    });
    
    if (button === ModalButtonType.Create && result){
      note.categoryId = result.id;
      this._activeCategoryId = result.id;
    }
  }

  @action.bound
  private async _toggleProtected(id: string, isProtected: boolean): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to ${isProtected ? 'unprotect' : 'protect'} note ${note.name}?`)) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/toggle-protect', null, {
      loader: this._loader
    });

    note.isProtected = !note.isProtected;
  }

  @action.bound
  private async _undoArchiveNote(id: string): Promise<void>{
    const note = this._notes.find(n => n.id === id);
    if (!note){
      return;
    }

    if (!await modalService.showConfirmation('Confirmation', `Do you really want to activate note ${note.name}?`)) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/undo-archive', null, {
      loader: this._loader
    });

    let activeNote: NoteInfo | undefined;
    const noteIndex = this._notes.findIndex(x => x.id === note.id);
    if (noteIndex !== -1){
      this._notes.splice(noteIndex, 1);
      activeNote = this._notes[noteIndex];
      this._activeNoteId = activeNote?.id;
      activeNote && this._setTitle(activeNote.name);
    }
    this._navigate?.(activeNote ? '/n/' + activeNote.id : '/notes');
  }

  @action.bound
  private async _handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const sortedNotes = [...this.Notes].sort((n1, n2) => (n1.sortOrder ?? 0) - (n2.sortOrder ?? 0));

    const oldIndex = sortedNotes.findIndex(n => n.id === active.id);
    const newIndex = sortedNotes.findIndex(n => n.id === over.id);

    ArrayService.arrayMoveMutable(sortedNotes, oldIndex, newIndex);
    sortedNotes.forEach((w, index) => w.sortOrder = index);

    const allSortedNotes = this._notes.sort((n1, n2) => n1.sortOrder - n2.sortOrder);
    const orderModel = {
      ids: allSortedNotes.map(w => w.id)
    };
    await apiService.put('/api/note/reorder', orderModel);
    notificationService.success('Order was saved!');
  }

  /* ----------------------------------------
     Top search → in-app search mode
     ---------------------------------------- */

  private _cancelSearch(): void {
    this._searchCancellationSource.abort();
    this._searchCancellationSource = new AbortController();
  }

  private _performSearch = async (): Promise<void> => {
    const text = searchService.query.trim();
    if (!text) {
      runInAction(() => {
        this._searchResults = null;
        this._isSearching = false;
      });
      return;
    }

    this._cancelSearch();
    runInAction(() => {
      this._isSearching = true;
      // On mobile the results render inside the fly-out drawer — make sure it's
      // open so the user can actually see the matches while typing.
      if (window.innerWidth <= 860) this._isMobileSidebarVisible = true;
    });

    const response = await apiService.post<{ results: AdvancedSearchResult[] }>(
      '/api/note/search/advanced',
      { text, isArchive: this._isArchive, useSemantic: searchService.useSemantic },
      { cancellationToken: this._searchCancellationSource.signal, loader: this._loader }
    );

    runInAction(() => {
      if (this._searchCancellationSource.signal.aborted) return;
      this._searchResults = response.results;
      this._isSearching = false;
    });
  };

  @action.bound
  private _onClearSearch(): void {
    searchService.clear();
    this._searchResults = null;
    this._isSearching = false;
    this._cancelSearch();
  }

  /** Opens the note but stays in search mode so the user can keep navigating
   *  the remaining matches. Search clears via Dashboard, a category, or the box's "x". */
  @action.bound
  private _onSelectSearchResult(noteId: string): void {
    this._activeNoteId = noteId;
    this._isUnlocked = false;
    const matched = this._searchResults?.find(r => r.noteId === noteId);
    this._setTitle(this._notes.find(n => n.id === noteId)?.name ?? matched?.name ?? '');
  }

  /* ----------------------------------------
     Pinned / Newest filters
     ---------------------------------------- */

  @action.bound
  private _onPinnedToggle(): void {
    this._onlyPinned = !this._onlyPinned;
  }

  @action.bound
  private _onNewestToggle(): void {
    this._sortNewest = !this._sortNewest;
  }

  @action.bound
  private async _togglePinned(id: string): Promise<void> {
    const note = this._notes.find(n => n.id === id);
    if (!note) return;

    await apiService.post<NoteInfo>('/api/note/' + id + '/toggle-pin', null, {
      loader: this._loader
    });

    runInAction(() => { note.isPinned = !note.isPinned; });
  }

  /* ----------------------------------------
     Category reordering (drag & drop)
     ---------------------------------------- */

  @action.bound
  private async _handleCategoryDragEnd(event: DragEndEvent): Promise<void> {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // Only real categories are sortable — 'default' is a fixed virtual row.
    const cats = this._categories
      .filter(c => c.id !== '' && c.id !== 'default')
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const oldIndex = cats.findIndex(c => c.id === active.id);
    const newIndex = cats.findIndex(c => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    ArrayService.arrayMoveMutable(cats, oldIndex, newIndex);

    const orderModel = { ids: cats.map(c => c.id) };
    await apiService.put('/api/note/category/reorder', orderModel);
    runInAction(() => {
      cats.forEach((c, index) => { c.sortOrder = index; });
      this._categories = this._appendDefaultCategory(cats, this._notes);
    });
    // Persist the new order to the shared rail (AppShell).
    void categoryRailStore.refresh();
  }

  /* ----------------------------------------
     New note page
     ---------------------------------------- */

  @action.bound
  private _onCancelNewNote(): void {
    // Cancel = browser Back. On a fresh entry (nothing to go back to) fall to the notes list.
    if (window.history.state && (window.history.state as { idx?: number }).idx === 0) {
      this._navigate?.('/notes');
    } else {
      this._navigate?.(-1);
    }
  }

  @action.bound
  private async _onSaveNewNote(params: { name: string, color: string, categoryId: string, tags?: string[] }): Promise<void> {
    const newNote = await apiService.post<NoteInfo>('/api/note', {
      name: params.name,
      color: params.color,
      categoryId: params.categoryId,
      tags: params.tags
    }, {
      loader: this._loader
    });

    runInAction(() => {
      this._isNewNoteOpen = false;
      this._notes.unshift(newNote);
      this._activeCategoryId = params.categoryId;
      this._activeNoteId = newNote.id;
      this._setTitle(newNote.name);
    });
    // Replace /n/new in the URL so "Back" doesn't land on the (now closed) form.
    this._navigate?.('/n/' + newNote.id, { replace: true });
  }
}